import { useEffect, useRef, useState } from "react";

import type { AvailabilityRow, DriverApi } from "./driver-api.ts";

/**
 * A courier's shift, kept alive on every screen rather than on one.
 *
 * ## The fault this closes
 *
 * Both heartbeats — the availability lease and the position report — lived inside `GoOnline`, the
 * readiness screen. That screen unmounts the moment a courier moves anywhere else, and where a
 * courier moves to is the offer inbox, because **waiting for work is the whole of their shift**.
 *
 * So the lease died on exactly the screen where it mattered. Measured on the deployment: a courier
 * went online, opened their offer inbox, a rider booked ninety seconds later, and the dispatch
 * failed `no_supply` on all three attempts. The availability row was 167 seconds old with its lease
 * expired, while the roster still read `online · available` and the courier's own phone said
 * *"Looking for jobs near you. Offers come to you automatically."*
 *
 * DRV-05's lease is sixty seconds and DSP-01 bounds `availabilityAge` at the same sixty, because an
 * offer to a courier whose application has stopped reporting goes into silence. A heartbeat that
 * stops when the courier navigates is a courier who is undispatchable precisely while they wait.
 *
 * ## Why a hook in the shell, reading the row itself
 *
 * A shift is a property of the session, not of a screen. Mounted once in `main.tsx`, it runs for as
 * long as the application is open and whatever the courier is looking at — which is what both
 * services already assume when they age these rows.
 *
 * It reads the availability row rather than being handed one, and derives *on shift* from
 * `presence` on that row. DRV-05 is the authority on whether a courier is online, so asking it is
 * both simpler than threading state down from a screen and correct when a second device, or a
 * dispatcher, changes the answer. One timer serves both cases: a tick either beats a live lease or
 * re-reads the row, so going online anywhere is picked up within the interval without the readiness
 * screen having to tell anybody.
 */

/** Half of DRV-05's sixty-second lease, so one lost request does not end a shift. */
const BEAT_MS = 25_000;

/**
 * A third of DRV-06's two-minute staleness window, so three observations fit inside it.
 *
 * The reasoning the position report has always used, carried over unchanged.
 */
const POSITION_BEAT_MS = 30_000;

export interface ShiftHeartbeatInput {
  readonly api: DriverApi;
  readonly driverId: string | undefined;
  /** `deviceId` and `sequence` belong to the device, not to this hook. */
  readonly device: () => { readonly deviceId: string; readonly sequence: number };
}

/**
 * Keeps DRV-05's lease and DRV-06's position alive while a courier is on shift.
 *
 * Returns the row it is beating from, so a screen sending an availability command can use the
 * version and lease this hook is advancing. Two components beating one row from two copies of its
 * version is a 409 on every other beat.
 */
export function useShiftHeartbeat(input: ShiftHeartbeatInput): AvailabilityRow | undefined {
  const { api, driverId, device } = input;
  const [row, setRow] = useState<AvailabilityRow | undefined>(undefined);

  /*
   * The row the timer reads, held in a ref so the interval is not torn down and rebuilt on every
   * beat. An effect that depended on `row` would restart its timer each time one returned, which
   * drifts the beat later and later until the lease it is extending has lapsed.
   */
  const rowRef = useRef<AvailabilityRow | undefined>(undefined);
  rowRef.current = row;

  /*
   * One place that records a new row, writing the ref **and** the state.
   *
   * The ref is not a cache of the state here, it is what the next beat reads. Writing only the
   * state would leave the ref holding the previous version until React re-rendered, and a beat that
   * fires in that gap carries a version DRV-05 has already moved past — which it fences, so every
   * beat after the first would be refused. Observed directly: three beats, one distinct
   * `expectedVersion` between them.
   */
  const record = (next: AvailabilityRow): void => {
    rowRef.current = next;
    setRow(next);
  };

  useEffect(() => {
    if (driverId === undefined) return undefined;
    let live = true;

    const read = (): void => {
      api
        .currentAvailability(driverId)
        .then((answer) => {
          if (live) record(answer);
        })
        // Silent. The readiness screen already draws the presence gate; this read exists so a beat
        // has a version, and a beat without one simply does not happen.
        .catch(() => undefined);
    };

    /*
     * One tick: beat a live lease, or find out whether there is one.
     *
     * The re-read arm is what makes this self-correcting. A courier who goes online on the
     * readiness screen, or whose lease is taken by another device, is picked up within one interval
     * with no screen having to notify this hook.
     */
    const tick = (): void => {
      const held = rowRef.current;
      const leaseId = held?.leaseId;
      if (held === undefined || leaseId === undefined || held.presence !== "online") {
        read();
        return;
      }
      void api
        .heartbeat({ driverId, expectedVersion: held.version, leaseId })
        .then((next) => {
          if (live) record(next);
        })
        .catch(() => {
          // A 409 means this application's lease is stale — somebody went offline, or a second
          // device took it. Re-reading is the only useful answer, and it is not an error to show a
          // courier: the readiness screen reports presence on its own.
          if (live) read();
        });
    };

    read();
    const timer = setInterval(tick, BEAT_MS);
    return (): void => {
      live = false;
      clearInterval(timer);
    };
  }, [api, driverId]);

  /* Where the courier is, while they are on shift. */
  useEffect(() => {
    if (driverId === undefined || row?.presence !== "online") return undefined;
    /*
     * A browser that offers no geolocation reports nothing and says nothing. The readiness screen
     * already names the gate this feeds, and a second message about one fact is worse than one.
     */
    if (globalThis.navigator?.geolocation === undefined) return undefined;

    let live = true;
    const send = (): void => {
      navigator.geolocation.getCurrentPosition(
        (here) => {
          if (!live) return;
          const identity = device();
          void api
            .recordPosition({
              sampleId: globalThis.crypto.randomUUID(),
              driverId,
              deviceId: identity.deviceId,
              sequence: identity.sequence,
              latitude: here.coords.latitude,
              longitude: here.coords.longitude,
              /*
               * The same worst-case rule `ToPickup` uses: `0` would claim perfect accuracy, and a
               * zero-radius circle over an unknown position is the one shape that must not be drawn.
               */
              horizontalAccuracyMetres: here.coords.accuracy > 0 ? here.coords.accuracy : 10_000,
              observedAt: new Date(here.timestamp).toISOString(),
            })
            .catch(() => undefined);
        },
        () => undefined,
        { enableHighAccuracy: true, maximumAge: 15_000, timeout: 10_000 },
      );
    };

    send();
    const timer = setInterval(send, POSITION_BEAT_MS);
    return (): void => {
      live = false;
      clearInterval(timer);
    };
  }, [api, device, driverId, row?.presence]);

  return row;
}
